import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ShuffleOutlinedIcon from "@mui/icons-material/ShuffleOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import axios from "axios";

import { useAuth } from "../../context/AuthProvider";
import Loading from "../../components/Loading";
import ResponseAlert from "../../components/ResponseAlert";
import { endPoint } from "../../global";

const prizeTypeLabel = {
  doorprize: "Doorprize",
  grandprize: "Grand Prize",
  guest_prize_1: "Doorprize Sesi I",
  guest_prize_2: "Doorprize Sesi II",
};

const prizeTypeOrder = {
  doorprize: 1,
  guest_prize_1: 2,
  guest_prize_2: 3,
  grandprize: 4,
};

const poppins = {
  fontFamily: "'Poppins', sans-serif",
};

const pageWrapper = {
  width: "100%",
  minHeight: "100%",
  position: "relative",
  zIndex: 1,
  px: { xs: 1.25, md: 1.6 },
  py: { xs: 1.1, md: 1.2 },
};

const glassPanel = {
  position: "relative",
  overflow: "hidden",
  borderRadius: "16px",
  background:
    "linear-gradient(145deg, rgba(8,22,51,0.82), rgba(15,45,92,0.52))",
  border: "1px solid rgba(147,197,253,0.24)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  boxShadow: "0 12px 32px rgba(0,0,0,0.26)",
};

const iconBox = {
  width: 34,
  height: 34,
  borderRadius: "11px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(96,165,250,0.16)",
  border: "1px solid rgba(147,197,253,0.28)",
  color: "#93c5fd",
  flexShrink: 0,
};

const goldIconBox = {
  ...iconBox,
  background: "rgba(251,191,36,0.14)",
  border: "1px solid rgba(251,191,36,0.34)",
  color: "#fbbf24",
};

const fieldStyle = {
  "& .MuiOutlinedInput-root": {
    ...poppins,
    color: "#fff",
    minHeight: 42,
    borderRadius: "13px",
    background:
      "linear-gradient(145deg, rgba(3,10,26,0.64), rgba(15,35,78,0.45))",
    border: "1px solid rgba(147,197,253,0.14)",
    boxShadow: "inset 0 0 28px rgba(96,165,250,0.07)",
    "& fieldset": {
      borderColor: "rgba(147,197,253,0.22)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(147,197,253,0.45)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#60a5fa",
      boxShadow: "0 0 14px rgba(96,165,250,0.28)",
    },
  },
  "& .MuiInputBase-input": {
    py: 1,
    fontSize: "0.82rem",
  },
  "& .MuiInputLabel-root": {
    ...poppins,
    color: "rgba(219,234,254,0.68)",
    fontSize: "0.82rem",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#93c5fd",
  },
  "& .MuiSvgIcon-root": {
    color: "#93c5fd",
  },
};

const autocompletePaperStyle = {
  paper: {
    sx: {
      mt: 0.7,
      borderRadius: "13px",
      background: "#fff",
      border: "1px solid rgba(147,197,253,0.24)",
      boxShadow: "0 16px 36px rgba(0,0,0,0.35)",
      color: "#0f172a",
    },
  },
  listbox: {
    sx: {
      py: 0.5,
      "& .MuiAutocomplete-option": {
        color: "#0f172a",
        fontFamily: "'Poppins', sans-serif",
        fontSize: "0.8rem",
        borderRadius: "10px",
        mx: 0.5,
        my: 0.2,
        minHeight: 42,
        "&:hover": {
          backgroundColor: "rgba(96,165,250,0.12)",
        },
        '&[aria-selected="true"]': {
          backgroundColor: "rgba(251,191,36,0.18)",
        },
        '&[aria-selected="true"].Mui-focused': {
          backgroundColor: "rgba(251,191,36,0.25)",
        },
        "&.Mui-focused": {
          backgroundColor: "rgba(96,165,250,0.16)",
        },
      },
    },
  },
};

const primaryButton = {
  ...poppins,
  borderRadius: "12px",
  fontWeight: 900,
  textTransform: "none",
  color: "#06111f",
  background: "linear-gradient(90deg, #fbbf24, #fde68a)",
  boxShadow: "0 0 18px rgba(251,191,36,0.2)",
  minHeight: 38,
  "&:hover": {
    background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.34)",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.1)",
  },
};

const ghostButton = {
  ...poppins,
  borderRadius: "11px",
  textTransform: "none",
  fontWeight: 900,
  color: "#dbeafe",
  background: "rgba(96,165,250,0.16)",
  border: "1px solid rgba(147,197,253,0.24)",
  boxShadow: "none",
  minHeight: 36,
  px: 1.4,
  py: 0.65,
  "&:hover": {
    background: "rgba(96,165,250,0.25)",
    boxShadow: "0 0 16px rgba(96,165,250,0.18)",
  },
};

const smallButton = {
  ...poppins,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 900,
  fontSize: "0.66rem",
  minHeight: 28,
  px: 0.8,
  py: 0.35,
};

export default function SetWin() {
  const { token } = useAuth();

  const [couponOptions, setCouponOptions] = useState([]);
  const [couponSearch, setCouponSearch] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);

  const [prizes, setPrizes] = useState([]);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const [formPrize, setFormPrize] = useState({
    prizeId: null,
    name: "",
    type: "doorprize",
    prizeOrder: 1,
  });

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const alertSuccess = (msg) => {
    setOpenAlert(true);
    setSuccess(true);
    setResponse(msg);
  };

  const alertError = (msg) => {
    setOpenAlert(true);
    setSuccess(false);
    setResponse(msg);
  };

  const sortedPrizes = useMemo(() => {
    return [...prizes].sort((a, b) => {
      const typeA = prizeTypeOrder[a.type] || 99;
      const typeB = prizeTypeOrder[b.type] || 99;

      if (typeA === typeB) {
        return Number(a.prizeOrder) - Number(b.prizeOrder);
      }

      return typeA - typeB;
    });
  }, [prizes]);

  const doorPrizes = useMemo(() => {
    return sortedPrizes.filter((item) => item.type === "doorprize");
  }, [sortedPrizes]);

  const grandPrizes = useMemo(() => {
    return sortedPrizes.filter((item) => item.type === "grandprize");
  }, [sortedPrizes]);

  const guestPrize1 = useMemo(() => {
    return sortedPrizes.filter((item) => item.type === "guest_prize_1");
  }, [sortedPrizes]);

  const guestPrize2 = useMemo(() => {
    return sortedPrizes.filter((item) => item.type === "guest_prize_2");
  }, [sortedPrizes]);

  const duplicateOrderKeys = useMemo(() => {
    const counts = {};

    prizes.forEach((item) => {
      const key = `${item.type}-${item.prizeOrder}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.keys(counts).filter((key) => counts[key] > 1);
  }, [prizes]);

  const isFormOrderDuplicate = useMemo(() => {
    return prizes.some((item) => {
      const sameType = item.type === formPrize.type;
      const sameOrder =
        Number(item.prizeOrder) === Number(formPrize.prizeOrder);
      const differentPrize = item.prizeId !== formPrize.prizeId;

      return sameType && sameOrder && differentPrize;
    });
  }, [prizes, formPrize]);

  const getCouponLabel = (option) => {
    if (!option) return "";

    return `${option.coupon || "-"} - ${option.name || "-"}`;
  };

  const searchCoupons = async (keyword) => {
    const q = keyword.trim();

    if (q.length < 2) {
      setCouponOptions([]);
      return;
    }

    setCouponLoading(true);

    try {
      const res = await axios.get(endPoint + "searchCoupon", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          q,
          limit: 30,
        },
      });

      setCouponOptions(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setCouponOptions([]);
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCoupons(couponSearch);
    }, 350);

    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponSearch]);

  const fetchPrizes = async () => {
    const res = await axios.get(endPoint + "getPrize", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    setPrizes(res.data.data || []);
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      await fetchPrizes();
    } catch (error) {
      setPrizes([]);
      alertError(
        error.response?.data?.message ||
          error.response?.data?.msg ||
          "Gagal mengambil data.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetCouponSelection = () => {
    setSelectedCoupon(null);
    setCouponOptions([]);
    setCouponSearch("");
  };

  const resetPrizeForm = () => {
    setFormPrize({
      prizeId: null,
      name: "",
      type: "doorprize",
      prizeOrder: 1,
    });
  };

  const handleEditPrize = (prize) => {
    setFormPrize({
      prizeId: prize.prizeId,
      name: prize.prizeName,
      type: prize.type,
      prizeOrder: prize.prizeOrder,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const savePrize = async () => {
    if (!formPrize.name || !formPrize.type || !formPrize.prizeOrder) {
      alertError("Nama hadiah, tipe, dan urutan wajib diisi.");
      return;
    }

    if (Number(formPrize.prizeOrder) <= 0) {
      alertError("Urutan hadiah minimal 1.");
      return;
    }

    if (isFormOrderDuplicate) {
      alertError(
        `Urutan ${formPrize.prizeOrder} untuk ${formPrize.type} sudah dipakai.`,
      );
      return;
    }

    setLoading(true);

    try {
      if (formPrize.prizeId) {
        const res = await axios.put(
          endPoint + "updatePrize",
          {
            prizeId: formPrize.prizeId,
            name: formPrize.name,
            type: formPrize.type,
            prizeOrder: Number(formPrize.prizeOrder),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        alertSuccess(res.data.msg || "Hadiah berhasil diubah.");
      } else {
        const res = await axios.post(
          endPoint + "createPrize",
          {
            name: formPrize.name,
            type: formPrize.type,
            prizeOrder: Number(formPrize.prizeOrder),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        alertSuccess(res.data.msg || "Hadiah berhasil ditambahkan.");
      }

      resetPrizeForm();
      await fetchPrizes();
    } catch (error) {
      alertError(error.response?.data?.msg || "Gagal menyimpan hadiah.");
    } finally {
      setLoading(false);
    }
  };

  const deletePrize = async (prize) => {
    const confirmDelete = window.confirm(
      `Yakin ingin menghapus hadiah "${prize.prizeName}"?`,
    );

    if (!confirmDelete) return;

    setLoading(true);

    try {
      const res = await axios.delete(endPoint + "deletePrize", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          prizeId: prize.prizeId,
        },
      });

      alertSuccess(res.data.msg || "Hadiah berhasil dihapus.");

      if (selectedPrize?.prizeId === prize.prizeId) {
        setSelectedPrize(null);
        resetCouponSelection();
      }

      await fetchPrizes();
    } catch (error) {
      alertError(error.response?.data?.msg || "Gagal menghapus hadiah.");
    } finally {
      setLoading(false);
    }
  };

  const setPrizeWinner = async () => {
    if (!selectedPrize?.prizeId) {
      alertError("Pilih hadiah terlebih dahulu.");
      return;
    }

    if (!selectedCoupon?.couponId) {
      alertError("Pilih kupon terlebih dahulu.");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(
        endPoint + "setPrizeWinner",
        {
          prizeId: selectedPrize.prizeId,
          couponId: selectedCoupon.couponId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      alertSuccess(res.data.msg || "Pemenang hadiah berhasil disetting.");

      setSelectedPrize(null);
      resetCouponSelection();

      await fetchPrizes();
    } catch (error) {
      alertError(error.response?.data?.msg || "Gagal setting pemenang hadiah.");
    } finally {
      setLoading(false);
    }
  };

  const clearPrizeWinner = async (prize) => {
    const confirmClear = window.confirm(
      `Kosongkan setting pemenang untuk "${prize.prizeName}"? Nanti hadiah ini akan random saat spin.`,
    );

    if (!confirmClear) return;

    setLoading(true);

    try {
      const res = await axios.post(
        endPoint + "setPrizeWinner",
        {
          prizeId: prize.prizeId,
          couponId: null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      alertSuccess(
        res.data.msg ||
          "Setting pemenang dikosongkan. Hadiah akan random saat spin.",
      );

      if (selectedPrize?.prizeId === prize.prizeId) {
        setSelectedPrize(null);
        resetCouponSelection();
      }

      await fetchPrizes();
    } catch (error) {
      alertError(error.response?.data?.msg || "Gagal mengosongkan pemenang.");
    } finally {
      setLoading(false);
    }
  };

  const resetWinner = async () => {
    const confirmReset = window.confirm(
      "Yakin ingin reset semua data pemenang testing?",
    );

    if (!confirmReset) return;

    setLoading(true);

    try {
      const res = await axios.get(endPoint + "resetWinner", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alertSuccess(res.data.msg || "Data pemenang berhasil di-reset.");

      setSelectedPrize(null);
      resetCouponSelection();

      await fetchPrizes();
    } catch (error) {
      alertError(error.response?.data?.msg || "Gagal reset data pemenang.");
    } finally {
      setLoading(false);
    }
  };

  const PrizeRow = ({ prize }) => {
    const isGrand = prize.type === "grandprize";
    const isGuest =
      prize.type === "guest_prize_1" || prize.type === "guest_prize_2";

    const hasWinner = Boolean(prize.couponId || prize.userId);

    const duplicateKey = `${prize.type}-${prize.prizeOrder}`;
    const isDuplicate = duplicateOrderKeys.includes(duplicateKey);
    const isSelected = selectedPrize?.prizeId === prize.prizeId;

    return (
      <Box
        sx={{
          p: 0.9,
          borderRadius: "13px",
          background: isSelected
            ? "rgba(251,191,36,0.18)"
            : hasWinner
              ? "rgba(34,197,94,0.1)"
              : isGrand
                ? "rgba(251,191,36,0.1)"
                : "rgba(96,165,250,0.08)",
          border: isSelected
            ? "1px solid rgba(251,191,36,0.75)"
            : isDuplicate
              ? "1px solid rgba(248,113,113,0.7)"
              : hasWinner
                ? "1px solid rgba(74,222,128,0.26)"
                : isGrand
                  ? "1px solid rgba(251,191,36,0.24)"
                  : "1px solid rgba(147,197,253,0.14)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "0.45fr 1.25fr 1.25fr 0.85fr",
            },
            gap: 0.7,
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{
                ...poppins,
                color: "rgba(219,234,254,0.56)",
                fontSize: "0.58rem",
                fontWeight: 700,
              }}
            >
              Urutan
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
              <Typography
                sx={{
                  ...poppins,
                  color: isDuplicate ? "#f87171" : "#fff",
                  fontSize: "0.98rem",
                  fontWeight: 900,
                }}
              >
                #{prize.prizeOrder}
              </Typography>

              {isDuplicate && (
                <Chip
                  label="Dobel"
                  size="small"
                  sx={{
                    ...poppins,
                    height: 20,
                    fontSize: "0.58rem",
                    color: "#fff",
                    fontWeight: 900,
                    background: "rgba(239,68,68,0.35)",
                    border: "1px solid rgba(248,113,113,0.45)",
                  }}
                />
              )}
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{
                ...poppins,
                color: "rgba(219,234,254,0.56)",
                fontSize: "0.58rem",
                fontWeight: 700,
              }}
            >
              Hadiah
            </Typography>

            <Typography
              sx={{
                ...poppins,
                color: isGrand ? "#fbbf24" : "#93c5fd",
                fontWeight: 900,
                fontSize: "0.8rem",
                mt: 0.15,
              }}
            >
              {prize.prizeName}
            </Typography>

            <Typography
              sx={{
                ...poppins,
                color: "rgba(255,255,255,0.52)",
                fontSize: "0.6rem",
                mt: 0.1,
              }}
            >
              {prizeTypeLabel[prize.type] || prize.type}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                ...poppins,
                color: "rgba(219,234,254,0.56)",
                fontSize: "0.58rem",
                fontWeight: 700,
              }}
            >
              Setting Pemenang
            </Typography>

            {hasWinner ? (
              <>
                <Typography
                  sx={{
                    ...poppins,
                    color: "#86efac",
                    fontWeight: 900,
                    fontSize: "0.76rem",
                    mt: 0.15,
                  }}
                >
                  {prize.winnerName || "-"}
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(255,255,255,0.62)",
                    fontSize: "0.6rem",
                    mt: 0.1,
                  }}
                >
                  {isGuest
                    ? `${prize.storeName || "-"}`
                    : `Kupon ${prize.coupon || "-"} • ${
                        prize.storeName || "-"
                      }`}
                </Typography>
              </>
            ) : (
              <Box
                sx={{
                  mt: 0.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.45,
                }}
              >
                <ShuffleOutlinedIcon sx={{ color: "#cbd5e1", fontSize: 15 }} />

                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(255,255,255,0.58)",
                    fontSize: "0.62rem",
                    fontWeight: 800,
                  }}
                >
                  Random saat spin
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 0.45,
            }}
          >
            <Button
              size="small"
              disabled={loading || isGuest}
              onClick={() => {
                setSelectedPrize(prize);
                resetCouponSelection();
              }}
              sx={{
                ...smallButton,
                color: isGuest ? "rgba(255,255,255,0.35)" : "#dbeafe",
                background: isSelected
                  ? "rgba(251,191,36,0.26)"
                  : "rgba(96,165,250,0.16)",
                border: isSelected
                  ? "1px solid rgba(251,191,36,0.55)"
                  : "1px solid rgba(147,197,253,0.22)",
                "&:hover": {
                  background: isSelected
                    ? "rgba(251,191,36,0.34)"
                    : "rgba(96,165,250,0.25)",
                },
                "&.Mui-disabled": {
                  color: "rgba(255,255,255,0.32)",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.08)",
                },
              }}
            >
              {isGuest ? "Auto" : isSelected ? "Dipilih" : "Set"}
            </Button>

            <Button
              size="small"
              disabled={loading}
              onClick={() => clearPrizeWinner(prize)}
              sx={{
                ...smallButton,
                color: "#e2e8f0",
                background: "rgba(148,163,184,0.12)",
                border: "1px solid rgba(203,213,225,0.14)",
                "&:hover": {
                  background: "rgba(148,163,184,0.2)",
                },
              }}
            >
              Random
            </Button>

            <Button
              size="small"
              disabled={loading}
              onClick={() => handleEditPrize(prize)}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
              sx={{
                ...smallButton,
                color: "#fbbf24",
                background: "rgba(251,191,36,0.12)",
                border: "1px solid rgba(251,191,36,0.22)",
                "&:hover": {
                  background: "rgba(251,191,36,0.2)",
                },
                "& .MuiButton-startIcon": {
                  mr: 0.4,
                },
              }}
            >
              Edit
            </Button>

            <Button
              size="small"
              disabled={loading}
              onClick={() => deletePrize(prize)}
              startIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: 15 }} />}
              sx={{
                ...smallButton,
                color: "#fecaca",
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(248,113,113,0.22)",
                "&:hover": {
                  background: "rgba(239,68,68,0.2)",
                },
                "& .MuiButton-startIcon": {
                  mr: 0.4,
                },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={pageWrapper}>
      <Box
        sx={{
          mb: 1.1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 0.8,
        }}
      >
        <Box>
          <Typography
            sx={{
              ...poppins,
              fontWeight: 900,
              fontSize: { xs: "1.22rem", md: "1.45rem" },
              color: "#fff",
              lineHeight: 1.15,
              textShadow: "0 0 18px rgba(96,165,250,0.38)",
            }}
          >
            Setting Hadiah & Pemenang
          </Typography>

          <Typography
            sx={{
              ...poppins,
              fontWeight: 400,
              fontSize: "0.7rem",
              color: "rgba(219,234,254,0.72)",
              mt: 0.25,
            }}
          >
            Kelola hadiah, atur pemenang, atau kosongkan agar random saat spin.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 0.65, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            disabled={loading}
            onClick={fetchData}
            startIcon={<RefreshOutlinedIcon />}
            sx={ghostButton}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={loading}
            onClick={resetWinner}
            startIcon={<RestartAltOutlinedIcon />}
            sx={{
              ...poppins,
              borderRadius: "11px",
              textTransform: "none",
              fontWeight: 900,
              minHeight: 36,
              px: 1.4,
              py: 0.65,
              background: "linear-gradient(90deg, #ef4444, #f87171)",
              boxShadow: "0 0 18px rgba(239,68,68,0.18)",
            }}
          >
            Reset Testing
          </Button>
        </Box>
      </Box>

      {duplicateOrderKeys.length > 0 && (
        <Box
          sx={{
            ...glassPanel,
            p: 1,
            mb: 1.1,
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(248,113,113,0.32)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
            <WarningAmberOutlinedIcon sx={{ color: "#f87171", fontSize: 20 }} />

            <Typography
              sx={{
                ...poppins,
                color: "#fecaca",
                fontWeight: 900,
                fontSize: "0.72rem",
              }}
            >
              Ada urutan hadiah yang dobel. Edit salah satu supaya spin tidak
              ambigu.
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            xl: "0.85fr 1.15fr",
          },
          gap: 1.1,
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "grid", gap: 1.1 }}>
          <Box
            sx={{
              ...glassPanel,
              p: { xs: 1.15, md: 1.35 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Box sx={goldIconBox}>
                <AddCircleOutlineOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>

              <Box>
                <Typography
                  sx={{
                    ...poppins,
                    fontWeight: 900,
                    fontSize: "0.88rem",
                    color: "#fff",
                  }}
                >
                  {formPrize.prizeId ? "Edit Hadiah" : "Tambah Hadiah"}
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    fontSize: "0.64rem",
                    color: "rgba(219,234,254,0.62)",
                    mt: 0.1,
                  }}
                >
                  Satu tipe hadiah tidak boleh punya urutan yang sama.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 1,
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 0.8,
              }}
            >
              <TextField
                label="Nama Hadiah"
                value={formPrize.name}
                onChange={(e) =>
                  setFormPrize((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                sx={fieldStyle}
              />

              <TextField
                select
                label="Tipe Hadiah"
                value={formPrize.type}
                onChange={(e) =>
                  setFormPrize((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }))
                }
                sx={fieldStyle}
              >
                <MenuItem value="doorprize">Doorprize</MenuItem>
                <MenuItem value="grandprize">Grand Prize</MenuItem>
                <MenuItem value="guest_prize_1">Doorprize Sesi I</MenuItem>
                <MenuItem value="guest_prize_2">Doorprize Sesi II</MenuItem>
              </TextField>

              <TextField
                label="Urutan"
                type="number"
                value={formPrize.prizeOrder}
                error={isFormOrderDuplicate}
                helperText={
                  isFormOrderDuplicate ? "Urutan ini sudah dipakai" : ""
                }
                onChange={(e) =>
                  setFormPrize((prev) => ({
                    ...prev,
                    prizeOrder: e.target.value,
                  }))
                }
                sx={fieldStyle}
              />
            </Box>

            <Box
              sx={{
                mt: 0.9,
                display: "flex",
                gap: 0.65,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="contained"
                disabled={loading || isFormOrderDuplicate}
                onClick={savePrize}
                startIcon={<SaveOutlinedIcon />}
                sx={{
                  ...primaryButton,
                  flex: 1,
                  py: 0.75,
                }}
              >
                {formPrize.prizeId ? "Simpan Perubahan" : "Tambah Hadiah"}
              </Button>

              {formPrize.prizeId && (
                <Button
                  variant="contained"
                  disabled={loading}
                  onClick={resetPrizeForm}
                  startIcon={<ClearOutlinedIcon />}
                  sx={{
                    ...ghostButton,
                    py: 0.75,
                  }}
                >
                  Batal
                </Button>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              ...glassPanel,
              p: { xs: 1.15, md: 1.35 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Box sx={goldIconBox}>
                <EmojiEventsOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>

              <Box>
                <Typography
                  sx={{
                    ...poppins,
                    fontWeight: 900,
                    fontSize: "0.88rem",
                    color: "#fff",
                  }}
                >
                  Set Pemenang
                </Typography>

                <Typography
                  sx={{
                    ...poppins,
                    fontSize: "0.64rem",
                    color: "rgba(219,234,254,0.62)",
                    mt: 0.1,
                  }}
                >
                  Pilih hadiah dari daftar kanan, lalu cari dan pilih kupon.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 1,
                p: 0.9,
                borderRadius: "13px",
                background: selectedPrize
                  ? "rgba(251,191,36,0.1)"
                  : "rgba(255,255,255,0.06)",
                border: selectedPrize
                  ? "1px solid rgba(251,191,36,0.24)"
                  : "1px solid rgba(147,197,253,0.14)",
              }}
            >
              <Typography
                sx={{
                  ...poppins,
                  color: "rgba(219,234,254,0.66)",
                  fontSize: "0.62rem",
                }}
              >
                Hadiah dipilih
              </Typography>

              <Typography
                sx={{
                  ...poppins,
                  color: selectedPrize ? "#fbbf24" : "rgba(255,255,255,0.6)",
                  fontWeight: 900,
                  fontSize: "0.82rem",
                  mt: 0.2,
                }}
              >
                {selectedPrize?.prizeName || "Belum pilih hadiah"}
              </Typography>

              {selectedPrize && (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(255,255,255,0.58)",
                    fontSize: "0.62rem",
                    mt: 0.15,
                  }}
                >
                  {prizeTypeLabel[selectedPrize.type] || selectedPrize.type} •
                  Urutan {selectedPrize.prizeOrder}
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 0.8 }}>
              <Autocomplete
                options={couponOptions}
                value={selectedCoupon}
                loading={couponLoading}
                inputValue={couponSearch}
                filterOptions={(options) => options}
                slotProps={autocompletePaperStyle}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCouponSearch(newInputValue);
                    setSelectedCoupon(null);
                  }

                  if (reason === "clear") {
                    resetCouponSelection();
                  }
                }}
                onChange={(event, newValue) => {
                  setSelectedCoupon(newValue);

                  if (newValue) {
                    setCouponSearch(getCouponLabel(newValue));
                  }
                }}
                getOptionLabel={getCouponLabel}
                isOptionEqualToValue={(option, value) => {
                  return (
                    String(option?.couponId || "") ===
                    String(value?.couponId || "")
                  );
                }}
                noOptionsText={
                  couponSearch.trim().length < 2
                    ? "Ketik minimal 2 karakter"
                    : "Kupon tidak ditemukan"
                }
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.couponId}
                    sx={{
                      ...poppins,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    <ConfirmationNumberOutlinedIcon
                      sx={{ color: "#fbbf24", fontSize: 18 }}
                    />

                    <Box>
                      <Typography
                        sx={{
                          ...poppins,
                          fontWeight: 800,
                          fontSize: "0.78rem",
                          color: "#0f172a",
                        }}
                      >
                        {option.coupon}
                      </Typography>

                      <Typography
                        sx={{
                          ...poppins,
                          fontSize: "0.68rem",
                          color: "rgba(15,23,42,0.72)",
                        }}
                      >
                        {option.name || "-"}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cari Kupon atau Nama"
                    variant="outlined"
                    sx={fieldStyle}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {couponLoading ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ width: "100%" }}
              />
            </Box>

            <Typography
              sx={{
                ...poppins,
                color:
                  selectedPrize && selectedCoupon
                    ? "#86efac"
                    : "rgba(255,255,255,0.62)",
                fontSize: "0.66rem",
                fontWeight: 700,
                mt: 0.75,
              }}
            >
              {!selectedPrize
                ? "Pilih hadiah dulu dari daftar kanan."
                : !selectedCoupon
                  ? "Cari dan pilih kupon dulu."
                  : `Siap set kupon ${selectedCoupon.coupon} untuk ${selectedPrize.prizeName}.`}
            </Typography>

            <Button
              variant="contained"
              disabled={loading}
              onClick={setPrizeWinner}
              startIcon={<EmojiEventsOutlinedIcon />}
              sx={{
                ...primaryButton,
                width: "100%",
                mt: 0.9,
                py: 0.75,
              }}
            >
              Set
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            ...glassPanel,
            p: { xs: 1.15, md: 1.35 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={goldIconBox}>
              <CardGiftcardOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  ...poppins,
                  fontWeight: 900,
                  fontSize: "0.88rem",
                  color: "#fff",
                }}
              >
                Daftar Hadiah
              </Typography>

              <Typography
                sx={{
                  ...poppins,
                  fontSize: "0.64rem",
                  color: "rgba(219,234,254,0.62)",
                  mt: 0.1,
                }}
              >
                Jika pemenang kosong, hadiah akan random saat spin.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 1 }}>
            <Typography
              sx={{
                ...poppins,
                color: "#93c5fd",
                fontWeight: 900,
                fontSize: "0.74rem",
                mb: 0.65,
              }}
            >
              Doorprize
            </Typography>

            <Box sx={{ display: "grid", gap: 0.65 }}>
              {doorPrizes.length ? (
                doorPrizes.map((prize) => (
                  <PrizeRow key={prize.prizeId} prize={prize} />
                ))
              ) : (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.62)",
                    fontSize: "0.7rem",
                  }}
                >
                  Belum ada data doorprize.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 1, borderColor: "rgba(147,197,253,0.14)" }} />

            <Typography
              sx={{
                ...poppins,
                color: "#fbbf24",
                fontWeight: 900,
                fontSize: "0.74rem",
                mb: 0.65,
              }}
            >
              Grand Prize
            </Typography>

            <Box sx={{ display: "grid", gap: 0.65 }}>
              {grandPrizes.length ? (
                grandPrizes.map((prize) => (
                  <PrizeRow key={prize.prizeId} prize={prize} />
                ))
              ) : (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.62)",
                    fontSize: "0.7rem",
                  }}
                >
                  Belum ada data grandprize.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 1, borderColor: "rgba(147,197,253,0.14)" }} />

            <Typography
              sx={{
                ...poppins,
                color: "#93c5fd",
                fontWeight: 900,
                fontSize: "0.74rem",
                mb: 0.65,
              }}
            >
              Doorprize Sesi I
            </Typography>

            <Box sx={{ display: "grid", gap: 0.65 }}>
              {guestPrize1.length ? (
                guestPrize1.map((prize) => (
                  <PrizeRow key={prize.prizeId} prize={prize} />
                ))
              ) : (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.62)",
                    fontSize: "0.7rem",
                  }}
                >
                  Belum ada data Doorprize Sesi I.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 1, borderColor: "rgba(147,197,253,0.14)" }} />

            <Typography
              sx={{
                ...poppins,
                color: "#93c5fd",
                fontWeight: 900,
                fontSize: "0.74rem",
                mb: 0.65,
              }}
            >
              Doorprize Sesi II
            </Typography>

            <Box sx={{ display: "grid", gap: 0.65 }}>
              {guestPrize2.length ? (
                guestPrize2.map((prize) => (
                  <PrizeRow key={prize.prizeId} prize={prize} />
                ))
              ) : (
                <Typography
                  sx={{
                    ...poppins,
                    color: "rgba(219,234,254,0.62)",
                    fontSize: "0.7rem",
                  }}
                >
                  Belum ada data Doorprize Sesi II.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Loading open={loading} />

      <ResponseAlert
        response={response}
        success={success}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
      />
    </Box>
  );
}
